import {useMemo} from 'react';
import {
  Card,
  Badge,
  Text,
  Box,
  Link,
  BlockStack,
  InlineStack,
  Divider,
} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {redirectToAdminUrl} from 'utilities/redirect';
import {useTruncatedList} from 'utilities/list-formatter';
import {validLocalesForMarket} from 'utilities/helper';
import {Market, ShopLocale} from 'utilities/use-shop-locales-and-markets';

interface Props {
  currentLocale: ShopLocale;
  markets: Market[];
  machineTranslationsLastBatchDate?: Date;
}

const scope = ['StatusCard'];

export function StatusCard({
  currentLocale,
  markets,
  machineTranslationsLastBatchDate,
}: Props) {
  const [i18n] = useI18n();

  const truncate = useTruncatedList({
    showTruncationToggle: false,
  });

  const status = useMemo(() => {
    if (currentLocale.primary) {
      return 'default';
    } else if (currentLocale.published) {
      return 'published';
    } else {
      return 'unpublished';
    }
  }, [currentLocale]);

  const scopeWithStatus = [...scope, status];

  const findMarketsContainingCurrentLocale = () => {
    if (!currentLocale.published || currentLocale.primary) return [];
    return markets
      .filter((market) =>
        validLocalesForMarket(market, markets).includes(currentLocale.locale),
      )
      .map((market) => market.name);
  };

  const getLanguageInfo = () => {
    const markets = findMarketsContainingCurrentLocale();
    if (status !== 'published') {
      return i18n.translate('info', {scope: scopeWithStatus});
    }

    if (markets.length === 0) {
      const marketsRedirect = () => {
        redirectToAdminUrl({
          adminUrl: '/settings/markets',
        });
      };

      return i18n.translate(
        'info.none',
        {scope: scopeWithStatus},
        {
          marketsLink: (
            <Link onClick={marketsRedirect}>
              {i18n.translate('market', {scope})}
            </Link>
          ),
        },
      );
    }

    return i18n.translate(
      'info',
      {
        scope: scopeWithStatus,
      },
      {
        count: markets.length,
        markets: truncate(markets),
      },
    );
  };

  const lastMachineTranslationBatchCard = machineTranslationsLastBatchDate ? (
    <>
      <Divider />
      <Box paddingBlockStart="400">
        <BlockStack gap="200">
          <p>
            <Badge tone="info">
              {i18n.translate('StatusCard.machineTranslationInfo.badge')}
            </Badge>
          </p>
          <Text as="p" variant="bodySm">
            {i18n.translate('StatusCard.machineTranslationInfo.info', {
              date: i18n.formatDate(machineTranslationsLastBatchDate, {
                dateStyle: 'full',
              }),
            })}
          </Text>
        </BlockStack>
      </Box>
    </>
  ) : null;

  return (
    <Card padding="400" roundedAbove="sm">
      <Box paddingBlockEnd={machineTranslationsLastBatchDate ? '400' : '0'}>
        <BlockStack gap="300" inlineAlign="start">
          <InlineStack gap="200">
            <Text as="h2" variant="headingSm">
              {currentLocale.name}
            </Text>
            <Badge tone={status === 'published' ? 'success' : undefined}>
              {i18n.translate('badge', {scope: scopeWithStatus})}
            </Badge>
          </InlineStack>
          <Text as="p" variant="bodySm">
            {getLanguageInfo()}
          </Text>
          <Link
            removeUnderline
            onClick={() => {
              redirectToAdminUrl({
                adminUrl: '/settings/languages',
              });
            }}
          >
            {i18n.translate('manage', {scope})}
          </Link>
        </BlockStack>
      </Box>
      {lastMachineTranslationBatchCard}
    </Card>
  );
}
